'use client'

import { useId, useLayoutEffect, useRef, useState } from 'react'

import {
  FLOATING_NOTICE_ICON_TINT,
  FloatingNotice,
} from '@/components/floating-notice'
import { X } from '@/components/icons'
import type { DeliveredAnnouncement } from '@/lib/announcements/delivery'
import { cn } from '@/lib/utils'

import { AnnouncementCta, AnnouncementIcon } from './announcement-parts'
import { useAnnouncementSeen } from './use-announcement-seen'

/**
 * Bodies longer than this always get a "Show more" toggle. Shorter ones get
 * it too whenever the clamped lines actually overflow — on a phone, 80
 * characters can — so no body is ever cut off with no way to read it.
 */
const COLLAPSE_AFTER = 160

/**
 * The persistent "What's New" banner (#14619): the training arrival
 * popover's floating top-center card (#14953), so it sits over the page
 * instead of pushing it down. The visual, the title, two lines of text
 * (expandable), the CTA and a dismiss control. A labelled region, so it is
 * reachable by landmark.
 *
 * `inline` renders it in place, for the admin preview's frame.
 */
export function AnnouncementBanner({
  announcement,
  alreadySeen,
  onSeen,
  onDismiss,
  onCta,
  onExpand,
  inline = false,
}: {
  announcement: DeliveredAnnouncement
  alreadySeen: boolean
  onSeen: () => void
  onDismiss: () => void
  onCta: () => void
  /** "Show more" was pressed (not "Show less"). */
  onExpand?: () => void
  inline?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const bodyId = useId()
  const bodyRef = useRef<HTMLParagraphElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  useAnnouncementSeen(ref, !alreadySeen, onSeen)

  // Measured only while clamped; once expanded the toggle must stay put so
  // "Show less" remains reachable.
  useLayoutEffect(() => {
    const element = bodyRef.current
    if (!element || expanded) return
    const measure = () =>
      setOverflowing(element.scrollHeight > element.clientHeight + 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [announcement.body, expanded])

  const collapsible =
    overflowing ||
    announcement.body.length > COLLAPSE_AFTER ||
    announcement.body.includes('\n')

  return (
    <FloatingNotice
      ref={ref}
      inline={inline}
      layer="belowDialogs"
      role="region"
      aria-labelledby={titleId}
      data-testid="announcement-banner"
      tileClassName={announcement.imageUrl ? 'p-0' : undefined}
      icon={
        announcement.imageUrl ? (
          // A plain <img>: the URL is a signed, short-lived storage URL that
          // next/image would proxy and cache past its expiry.
          <img
            src={announcement.imageUrl}
            alt=""
            className="h-8 w-8 rounded-lg object-cover"
          />
        ) : (
          <AnnouncementIcon
            iconName={announcement.iconName}
            className={cn('h-4 w-4', FLOATING_NOTICE_ICON_TINT)}
          />
        )
      }
      controls={
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss announcement"
          className="flex pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px] items-center justify-center rounded-lg p-2 text-text-secondary transition-colors hover:text-text-secondary"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      }
    >
      {/* A <p>, not a heading: the root layout renders this on every page,
          ahead of the page's own h1. */}
      <p id={titleId} className="text-sm font-semibold text-text-primary">
        {announcement.title}
      </p>
      <p
        ref={bodyRef}
        id={bodyId}
        className={cn(
          'mt-1 text-sm text-text-secondary',
          expanded ? 'whitespace-pre-line' : 'line-clamp-2',
        )}
      >
        {announcement.body}
      </p>
      {collapsible && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={() => {
            if (!expanded) onExpand?.()
            setExpanded(!expanded)
          }}
          className="mt-1 text-sm font-medium text-text-link hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
      <div className="mt-3 flex justify-end empty:hidden">
        <AnnouncementCta
          label={announcement.ctaLabel}
          url={announcement.ctaUrl}
          onActivate={onCta}
        />
      </div>
    </FloatingNotice>
  )
}
